import useAuthStore from "@/store/useAuthStore";
import {
  createFileRoute,
  Link,
  retainSearchParams,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import { ApiService } from "@/services/api-service";
import { Loader2 } from "lucide-react";

type AuthRedirectStatus = "loading" | "error" | "success";

const searchSchema = z.object({
  token: z.string().optional(),
  refreshToken: z.string().optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/auth/_layout/redirect")({
  component: AuthRedirectComponent,
  search: {
    middlewares: [retainSearchParams(["token", "redirect"])],
  },
  validateSearch: zodValidator(searchSchema),
});

function AuthRedirectComponent() {
  const { login } = useAuthStore();
  const {
    token: encodedToken,
    redirect: customRedirect,
    refreshToken: encodedRefreshToken,
  } = Route.useSearch();
  const token = encodedToken ? decodeURIComponent(encodedToken) : null;
  const refreshToken = encodedRefreshToken
    ? decodeURIComponent(encodedRefreshToken)
    : null;
  const [status, setStatus] = useState<AuthRedirectStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      console.error("No token provided in redirect URL");
      navigate({ to: "/auth/login" });
      return;
    }

    async function authenticateUser() {
      try {
        const response = await ApiService.verifyGoogleSigninToken(token!);
        if (!isMounted) return;

        const user = response.user;

        const project = user?.user_profile?.project?.[0] || null;

        login(user, {
          token,
          refreshToken,
        });

        if (response.newlyVerified) {
          navigate({
            to: "/app/editor/$projectId",
            params: { projectId: project?.id },
            search: {
              type: "personalization",
            },
            replace: true,
          });
        } else {
          navigate({
            to: customRedirect || "/app",
            replace: true,
          });
        }

        setStatus("success");
      } catch (error) {
        if (!isMounted) return;

        const apiError = error as {
          response?: { data?: { message?: string } };
        };
        const errorMsg =
          apiError?.response?.data?.message ||
          "Authentication failed. Please try again.";

        setErrorMessage(errorMsg);
        setStatus("error");
        console.error("Authentication error:", error);
      }
    }

    authenticateUser();

    return () => {
      isMounted = false;
    };
  }, [token, login, navigate, customRedirect, refreshToken]);

  return (
    <div className="flex flex-col items-center justify-center">
      {status === "loading" && (
        <>
          <Loader2 className="h-8 w-8 text-secondary-700 animate-spin" />
          <h2 className="text-base font-semibold text-gray-700">
            Authenticating...
          </h2>
          <p className="text-gray-500 mt-2">
            Please wait while we verify your credentials
          </p>
        </>
      )}

      {status === "error" && (
        <div className="text-center p-6 rounded-lg shadow-md w-xl">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-base font-bold text-red-600 mb-2">
            Authentication Error
          </h2>
          <p className="text-gray-700 mb-4">{errorMessage}</p>
          <Link to={"/auth/login"} className="underline" replace>
            Login
          </Link>
        </div>
      )}

      {status === "success" && (
        <div className="flex flex-col justify-center items-center gap-y-2">
          <h2 className="text-base font-semibold text-gray-700">
            Successfully authenticated!
          </h2>
          <p className="text-gray-500 mt-2">Redirecting you now...</p>
        </div>
      )}
    </div>
  );
}
